import { useState } from "react";
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import ArticleIcon from '@mui/icons-material/ArticleOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import TagIcon from '@mui/icons-material/LocalOfferOutlined';
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import EventIcon from '@mui/icons-material/CalendarTodayRounded';

import api from "../../services/api";

const getTomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
};

const TONES = ['Professionale', 'Creativo', 'Informativo', 'Persuasivo', 'Formale', 'Casual'];
const AUDIENCES = ['Professionisti', 'Manager', 'Imprenditori', 'Consumatori', 'Tecnici', 'Generale'];
const LANGUAGES = ['Italiano', 'English', 'Español', 'Français', 'Deutsch'];

// ─── Markdown renderer ────────────────────────────────────────────────────────

function parseInline(text) {
    return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>');
}

const ArticleMarkdown = ({ content }) => {
    if (!content) return null;
    const lines = content.split('\n');
    const elements = [];
    let i = 0;
    let listBuffer = [];
    let listType = null;

    const flushList = (key) => {
        if (listBuffer.length === 0) return;
        const Tag = listType === 'ol' ? 'ol' : 'ul';
        elements.push(
            <Tag key={`list-${key}`} style={{ margin: '0.5em 0 1em 1.5em', paddingLeft: 0, lineHeight: 1.8 }}>
                {listBuffer.map((item, idx) => (
                    <li key={idx} style={{ fontSize: '0.92rem', color: '#2D2D3F', marginBottom: 4 }}
                        dangerouslySetInnerHTML={{ __html: parseInline(item) }} />
                ))}
            </Tag>
        );
        listBuffer = [];
        listType = null;
    };

    while (i < lines.length) {
        const line = lines[i];

        if (line.startsWith('## ')) {
            flushList(i);
            elements.push(
                <h2 key={i} style={{ fontSize: '1.15rem', fontWeight: 800, color: '#1C1C28', margin: '1.8em 0 0.6em', lineHeight: 1.3, borderBottom: '2px solid rgba(59,130,246,0.15)', paddingBottom: '0.3em' }}
                    dangerouslySetInnerHTML={{ __html: parseInline(line.slice(3)) }} />
            );
        } else if (line.startsWith('### ')) {
            flushList(i);
            elements.push(
                <h3 key={i} style={{ fontSize: '1rem', fontWeight: 700, color: '#2D2D3F', margin: '1.4em 0 0.4em', lineHeight: 1.4 }}
                    dangerouslySetInnerHTML={{ __html: parseInline(line.slice(4)) }} />
            );
        } else if (line.startsWith('# ')) {
            flushList(i);
            elements.push(
                <h1 key={i} style={{ fontSize: '1.3rem', fontWeight: 800, color: '#1C1C28', margin: '0 0 0.8em', lineHeight: 1.2 }}
                    dangerouslySetInnerHTML={{ __html: parseInline(line.slice(2)) }} />
            );
        } else if (line.startsWith('> ')) {
            flushList(i);
            elements.push(
                <blockquote key={i} style={{ margin: '1em 0', padding: '0.6em 1.2em', borderLeft: '3px solid rgba(59,130,246,0.5)', background: 'rgba(59,130,246,0.05)', borderRadius: '0 8px 8px 0', fontStyle: 'italic', color: '#4A4A5A' }}>
                    <span dangerouslySetInnerHTML={{ __html: parseInline(line.slice(2)) }} />
                </blockquote>
            );
        } else if (/^[-*] /.test(line)) {
            if (listType !== 'ul') { flushList(i); listType = 'ul'; }
            listBuffer.push(line.slice(2));
        } else if (/^\d+\. /.test(line)) {
            if (listType !== 'ol') { flushList(i); listType = 'ol'; }
            listBuffer.push(line.replace(/^\d+\. /, ''));
        } else if (line.trim() === '') {
            flushList(i);
            elements.push(<div key={i} style={{ height: '0.7em' }} />);
        } else {
            flushList(i);
            elements.push(
                <p key={i} style={{ fontSize: '0.92rem', color: '#2D2D3F', lineHeight: 1.85, margin: '0 0 0.3em' }}
                    dangerouslySetInnerHTML={{ __html: parseInline(line) }} />
            );
        }
        i++;
    }
    flushList('end');
    return <>{elements}</>;
};

// ─── Word download ────────────────────────────────────────────────────────────

function markdownToHtml(md) {
    const lines = md.split('\n');
    let html = '';
    let inUl = false, inOl = false;
    lines.forEach(line => {
        if (line.startsWith('## ')) {
            if (inUl) { html += '</ul>'; inUl = false; }
            if (inOl) { html += '</ol>'; inOl = false; }
            html += `<h2>${parseInline(line.slice(3))}</h2>`;
        } else if (line.startsWith('### ')) {
            if (inUl) { html += '</ul>'; inUl = false; }
            if (inOl) { html += '</ol>'; inOl = false; }
            html += `<h3>${parseInline(line.slice(4))}</h3>`;
        } else if (line.startsWith('# ')) {
            if (inUl) { html += '</ul>'; inUl = false; }
            if (inOl) { html += '</ol>'; inOl = false; }
            html += `<h1>${parseInline(line.slice(2))}</h1>`;
        } else if (line.startsWith('> ')) {
            if (inUl) { html += '</ul>'; inUl = false; }
            if (inOl) { html += '</ol>'; inOl = false; }
            html += `<blockquote style="border-left:3px solid #3B82F6;margin:1em 0;padding:0.5em 1em;font-style:italic;color:#555">${parseInline(line.slice(2))}</blockquote>`;
        } else if (/^[-*] /.test(line)) {
            if (!inUl) { if (inOl) { html += '</ol>'; inOl = false; } html += '<ul>'; inUl = true; }
            html += `<li>${parseInline(line.slice(2))}</li>`;
        } else if (/^\d+\. /.test(line)) {
            if (!inOl) { if (inUl) { html += '</ul>'; inUl = false; } html += '<ol>'; inOl = true; }
            html += `<li>${parseInline(line.replace(/^\d+\. /, ''))}</li>`;
        } else if (line.trim() === '') {
            if (inUl) { html += '</ul>'; inUl = false; }
            if (inOl) { html += '</ol>'; inOl = false; }
            html += '<br/>';
        } else {
            if (inUl) { html += '</ul>'; inUl = false; }
            if (inOl) { html += '</ol>'; inOl = false; }
            html += `<p>${parseInline(line)}</p>`;
        }
    });
    if (inUl) html += '</ul>';
    if (inOl) html += '</ol>';
    return html;
}

function downloadWord(result) {
    const contentHtml = markdownToHtml(result.content || '');
    const metaHtml = result.meta_description ? `<p style="font-style:italic;color:#666;">${result.meta_description}</p><br/>` : '';
    const tagsHtml = result.tags?.length ? `<p><strong>Tag:</strong> ${result.tags.join(', ')}</p>` : '';
    const docHtml = `
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"/><title>${result.title || 'Articolo'}</title>
<style>
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; line-height: 1.6; margin: 2cm; color: #222; }
  h1 { font-size: 20pt; color: #1C1C28; margin-bottom: 6pt; }
  h2 { font-size: 15pt; color: #1C1C28; margin-top: 18pt; margin-bottom: 6pt; border-bottom: 1px solid #ccc; padding-bottom: 3pt; }
  h3 { font-size: 12pt; color: #333; margin-top: 14pt; }
  p { margin: 0 0 8pt; }
  ul, ol { margin: 0 0 10pt 20pt; }
  li { margin-bottom: 4pt; }
  blockquote { border-left: 3px solid #3B82F6; margin: 10pt 0; padding: 4pt 12pt; background: #f0f5ff; color: #555; font-style: italic; }
  code { font-family: Courier New; background: #f5f5f5; padding: 1pt 3pt; }
  strong { font-weight: bold; }
  em { font-style: italic; }
</style></head>
<body>
<h1>${result.title || ''}</h1>
${metaHtml}
${contentHtml}
${tagsHtml}
</body></html>`;

    const blob = new Blob(['\ufeff', docHtml], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(result.title || 'articolo').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// ─── Component ────────────────────────────────────────────────────────────────

const ModernArticleGenerator = () => {
    const { t } = useTranslation();
    const LENGTHS = [
        { value: 'short', label: t('article.lengths.short') },
        { value: 'medium', label: t('article.lengths.medium') },
        { value: 'long', label: t('article.lengths.long') },
    ];
    const [topic, setTopic] = useState('');
    const [tone, setTone] = useState('Professionale');
    const [length, setLength] = useState('medium');
    const [audience, setAudience] = useState('Professionisti');
    const [language, setLanguage] = useState('Italiano');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [copied, setCopied] = useState(false);
    const [imageError, setImageError] = useState(null);
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
    const [calOpen, setCalOpen] = useState(false);
    const [calTitle, setCalTitle] = useState('');
    const [calDate, setCalDate] = useState(getTomorrow());
    const [calSaving, setCalSaving] = useState(false);

    const handleGenerate = async () => {
        if (!topic.trim()) return;
        setLoading(true);
        setResult(null);
        setGeneratedImage(null);
        setImageError(null);
        try {
            const resp = await api.post('/api/openai/autopilot/', {
                step: 'article',
                payload: { title: topic, tone, length, audience, language },
            });
            setResult(resp.data.result);
        } catch (err) {
            if (err?.response?.status === 402) {
                window.dispatchEvent(new CustomEvent('admind_limit_exceeded', { detail: err.response.data || {} }));
                setLoading(false);
                return;
            }
            setResult({ error: err?.response?.data?.error || t('article.error_default') });
        }
        setLoading(false);
    };

    const handleGenerateImage = async () => {
        if (!result) return;
        setImageLoading(true);
        setImageError(null);
        try {
            const imagePrompt = `Professional editorial cover image for an article titled "${result.title}". Modern, minimalist style, high quality photography or illustration. No text in the image.`;
            const resp = await api.post('/api/openai/dalle-v2/', {
                prompt: imagePrompt,
                size: '1792x1024',
                model: 'dall-e-3',
                style: 'vivid',
                quality: 'standard',
            });
            const imgUrl = resp.data?.url || resp.data?.image_url;
            if (imgUrl) {
                setGeneratedImage(imgUrl);
            } else {
                setImageError(t('article.error_no_image'));
            }
        } catch (err) {
            setImageError(err?.response?.data?.error || t('article.error_default'));
        }
        setImageLoading(false);
    };

    const handleCopy = () => {
        if (result?.content) {
            navigator.clipboard.writeText(`# ${result.title}\n\n${result.content}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleOpenCal = () => {
        setCalTitle(result?.title || topic || 'Articolo');
        setCalDate(getTomorrow());
        setCalOpen(true);
    };

    const handleSaveToCal = async () => {
        if (!calTitle.trim() || !calDate) return;
        setCalSaving(true);
        try {
            const text = result?.content ? `# ${result.title}\n\n${result.content}` : '';
            await api.post('/api/openai/calendar/', {
                title: calTitle.trim(),
                content_type: 'article',
                scheduled_date: calDate,
                content_text: text,
                image_url: '',
                status: 'planned',
            });
            setCalOpen(false);
            setSnack({ open: true, msg: t('article.snack_cal_ok'), severity: 'success' });
        } catch (_) {
            setSnack({ open: true, msg: t('article.snack_cal_err'), severity: 'error' });
        }
        setCalSaving(false);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', overflow: 'hidden', backgroundColor: '#FAFAF8' }}>

            {/* Sidebar */}
            <Box sx={{ width: 300, flexShrink: 0, borderRight: '1px solid rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', overflow: 'auto', backgroundColor: '#F5F4FF', p: 2.5, gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'linear-gradient(135deg, #3B82F6, #60A5FA)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ArticleIcon sx={{ color: '#fff', fontSize: '1.2rem' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, color: '#1C1C28', fontSize: '0.95rem' }}>{t('article.title')}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#6B6B7B' }}>{t('article.subtitle')}</Typography>
                    </Box>
                </Box>

                <TextField fullWidth label={t('article.field_topic')} value={topic} onChange={(e) => setTopic(e.target.value)}
                    multiline rows={3} size="small" placeholder={t('article.topic_placeholder')} />

                <FormControl size="small" fullWidth>
                    <InputLabel>{t('article.field_tone')}</InputLabel>
                    <Select value={tone} label={t('article.field_tone')} onChange={(e) => setTone(e.target.value)}>
                        {TONES.map((tn, i) => <MenuItem key={tn} value={tn}>{t('article.tones', { returnObjects: true })[i] || tn}</MenuItem>)}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                    <InputLabel>{t('article.field_length')}</InputLabel>
                    <Select value={length} label={t('article.field_length')} onChange={(e) => setLength(e.target.value)}>
                        {LENGTHS.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                    <InputLabel>{t('article.field_audience')}</InputLabel>
                    <Select value={audience} label={t('article.field_audience')} onChange={(e) => setAudience(e.target.value)}>
                        {AUDIENCES.map((a, i) => <MenuItem key={a} value={a}>{t('article.audiences', { returnObjects: true })[i] || a}</MenuItem>)}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                    <InputLabel>{t('article.field_language')}</InputLabel>
                    <Select value={language} label={t('article.field_language')} onChange={(e) => setLanguage(e.target.value)}>
                        {LANGUAGES.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </Select>
                </FormControl>

                <Button fullWidth variant="contained" onClick={handleGenerate} disabled={loading || !topic.trim()}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                    sx={{ py: 1.3, background: 'linear-gradient(135deg, #3B82F6, #60A5FA)', '&:hover': { background: 'linear-gradient(135deg, #2563EB, #3B82F6)' }, textTransform: 'none', fontWeight: 700 }}>
                    {t(loading ? 'article.btn_generating' : 'article.btn_generate')}
                </Button>
            </Box>

            {/* Article area */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3, backgroundColor: '#FAFAF8', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: 4 } }}>
                {!result && !loading && (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <Box sx={{ width: 72, height: 72, borderRadius: '20px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ArticleIcon sx={{ fontSize: '2rem', color: '#60A5FA', opacity: 0.7 }} />
                        </Box>
                        <Typography sx={{ color: '#6B6B7B', fontSize: '0.9rem', fontWeight: 500 }}>{t('article.empty_title')}</Typography>
                        <Typography sx={{ color: '#9B9BA8', fontSize: '0.8rem' }}>{t('article.empty_sub')}</Typography>
                    </Box>
                )}

                {loading && (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress sx={{ color: '#3B82F6' }} />
                        <Typography sx={{ color: '#6B6B7B', fontSize: '0.9rem' }}>{t('article.loading_title')}</Typography>
                        <Typography sx={{ color: '#9B9BA8', fontSize: '0.8rem' }}>{t('article.loading_sub')}</Typography>
                    </Box>
                )}

                {result?.error && (
                    <Box sx={{ p: 3, borderRadius: '16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                        <Typography sx={{ color: '#EF4444' }}>❌ {result.error}</Typography>
                    </Box>
                )}

                {result && !result.error && (
                    <Box sx={{ maxWidth: 820, mx: 'auto' }}>
                        {/* Header row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h2" sx={{ color: '#1C1C28', mb: 0.8, fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.25 }}>
                                    {result.title}
                                </Typography>
                                {result.meta_description && (
                                    <Typography sx={{ color: '#6B6B7B', fontSize: '0.85rem', fontStyle: 'italic', lineHeight: 1.5 }}>
                                        {result.meta_description}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.8, flexShrink: 0 }}>
                                <Tooltip title={t(copied ? 'article.copy_copied' : 'article.copy')}>
                                    <IconButton onClick={handleCopy} sx={{ color: copied ? '#10B981' : '#6B6B7B', border: '1px solid rgba(0,0,0,0.09)', borderRadius: '10px', p: 0.8, '&:hover': { borderColor: 'rgba(59,130,246,0.3)', background: 'rgba(59,130,246,0.05)' } }}>
                                        {copied ? <CheckIcon sx={{ fontSize: '1.1rem' }} /> : <ContentCopyIcon sx={{ fontSize: '1.1rem' }} />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('article.download_word')}>
                                    <IconButton onClick={() => downloadWord(result)} sx={{ color: '#6B6B7B', border: '1px solid rgba(0,0,0,0.09)', borderRadius: '10px', p: 0.8, '&:hover': { borderColor: 'rgba(112,18,128,0.3)', background: 'rgba(112,18,128,0.05)' } }}>
                                        <DownloadIcon sx={{ fontSize: '1.1rem' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('article.regenerate')}>
                                    <IconButton onClick={handleGenerate} sx={{ color: '#6B6B7B', border: '1px solid rgba(0,0,0,0.09)', borderRadius: '10px', p: 0.8, '&:hover': { borderColor: 'rgba(59,130,246,0.3)', background: 'rgba(59,130,246,0.05)' } }}>
                                        <RefreshIcon sx={{ fontSize: '1.1rem' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t(imageLoading ? 'article.img_generating_tip' : 'article.img_generate_tip')}>
                                    <span>
                                        <IconButton onClick={handleGenerateImage} disabled={imageLoading} sx={{ color: '#6B6B7B', border: '1px solid rgba(0,0,0,0.09)', borderRadius: '10px', p: 0.8, '&:hover': { borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.05)' } }}>
                                            {imageLoading ? <CircularProgress size={16} /> : <ImageIcon sx={{ fontSize: '1.1rem' }} />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t('article.add_to_cal')}>
                                    <IconButton onClick={handleOpenCal} sx={{ color: '#6B6B7B', border: '1px solid rgba(0,0,0,0.09)', borderRadius: '10px', p: 0.8, '&:hover': { borderColor: 'rgba(112,18,128,0.3)', background: 'rgba(112,18,128,0.05)', color: '#701280' } }}>
                                        <EventIcon sx={{ fontSize: '1.1rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Cover image */}
                        {generatedImage && (
                            <Box sx={{ mb: 3, borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                                <img src={generatedImage} alt="Cover" style={{ width: '100%', height: 'auto', display: 'block' }} />
                            </Box>
                        )}

                        {/* Article content with real Markdown */}
                        <Box sx={{ p: 3.5, borderRadius: '16px', background: '#fff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 8px rgba(0,0,0,0.04)', mb: 2,
                            '& code': { background: 'rgba(59,130,246,0.08)', color: '#3B82F6', px: '4px', py: '1px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.85em' },
                        }}>
                            <ArticleMarkdown content={result.content} />
                        </Box>

                        {/* Tags */}
                        {result.tags && result.tags.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, alignItems: 'center', mt: 1 }}>
                                <TagIcon sx={{ fontSize: '0.9rem', color: '#9B9BA8' }} />
                                {result.tags.map(tag => (
                                    <Chip key={tag} label={tag} size="small"
                                        sx={{ backgroundColor: 'rgba(59,130,246,0.10)', color: '#3B82F6', border: '1px solid rgba(59,130,246,0.2)', fontWeight: 500, fontSize: '0.75rem' }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Snackbar for image errors */}
            <Snackbar open={Boolean(imageError)} autoHideDuration={5000} onClose={() => setImageError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setImageError(null)} sx={{ borderRadius: '10px' }}>
                    {t('article.img_cover_err', { err: imageError })}
                </Alert>
            </Snackbar>

            {/* Snackbar for calendar feedback */}
            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: '10px' }}>
                    {snack.msg}
                </Alert>
            </Snackbar>

            {/* Calendar Dialog */}
            <Dialog open={calOpen} onClose={() => !calSaving && setCalOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>{t('article.cal_dialog_title')}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        fullWidth label={t('article.cal_field_title')} size="small" value={calTitle}
                        onChange={e => setCalTitle(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                    <TextField
                        fullWidth label={t('article.cal_field_date')} type="date" size="small" value={calDate}
                        onChange={e => setCalDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setCalOpen(false)} disabled={calSaving} sx={{ color: '#6B6B7B', textTransform: 'none' }}>
                        {t('article.cal_cancel')}
                    </Button>
                    <Button
                        variant="contained" onClick={handleSaveToCal}
                        disabled={!calTitle.trim() || !calDate || calSaving}
                        startIcon={calSaving ? <CircularProgress size={14} color="inherit" /> : <EventIcon />}
                        sx={{ textTransform: 'none', borderRadius: '10px', background: 'linear-gradient(135deg, #701280, #9C27B0)', '&:hover': { background: 'linear-gradient(135deg, #5a0e68, #7B1FA2)' } }}
                    >
                        {t(calSaving ? 'article.cal_saving' : 'article.cal_save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ModernArticleGenerator;
